import type { GraphNodeAnchor } from '../../../../core/entities/graph-document';
import type { NodeType } from '../../../../core/entities/node';

export type SceneNodeDepthBucket = 'focus' | 'near' | 'mid' | 'far';
export type SceneNodeHaloStrength = 'none' | 'soft' | 'focus';
export type SceneNodeEdgeEmphasis = 'none' | 'related' | 'top3';

export interface Scene3DNodePresentationInput {
	nodeId: string;
	type: NodeType;
	displayName: string;
	anchor: GraphNodeAnchor;
	isFocused?: boolean;
	isNeighbor?: boolean;
}

export interface Scene3DDepthRange {
	minimumZ: number;
	maximumZ: number;
	visibleNodeCount: number;
}

export interface Scene3DNodePresentation {
	nodeId: string;
	depthBucket: SceneNodeDepthBucket;
	radiusPx: number;
	showLabel: boolean;
	showTypeTag: boolean;
	haloStrength: SceneNodeHaloStrength;
	edgeEmphasis: SceneNodeEdgeEmphasis;
}

export function createScene3DDepthRange(anchors: GraphNodeAnchor[]): Scene3DDepthRange {
	if (anchors.length === 0) {
		return { minimumZ: 0, maximumZ: 0, visibleNodeCount: 0 };
	}

	return anchors.reduce<Scene3DDepthRange>(
		(accumulator, anchor) => ({
			minimumZ: Math.min(accumulator.minimumZ, anchor.z),
			maximumZ: Math.max(accumulator.maximumZ, anchor.z),
			visibleNodeCount: accumulator.visibleNodeCount + 1,
		}),
		{ minimumZ: Number.POSITIVE_INFINITY, maximumZ: Number.NEGATIVE_INFINITY, visibleNodeCount: 0 }
	);
	}

export function createScene3DNodePresentation(
	input: Scene3DNodePresentationInput,
	depthRange: Scene3DDepthRange,
): Scene3DNodePresentation {
	const depthProgress = normalizeDepth(input.anchor.z, depthRange.minimumZ, depthRange.maximumZ);
	const depthBucket = classifyDepthBucket(depthProgress, Boolean(input.isFocused));

	return {
		nodeId: input.nodeId,
		depthBucket,
		radiusPx: radiusForDepth(depthBucket),
		showLabel: input.isFocused || depthBucket === 'near' || (Boolean(input.isNeighbor) && depthBucket !== 'far'),
		showTypeTag: input.isFocused || depthBucket === 'focus',
		haloStrength: input.isFocused ? 'focus' : input.isNeighbor ? 'soft' : 'none',
		edgeEmphasis: input.isFocused ? 'top3' : input.isNeighbor ? 'related' : 'none',
	};
}

function classifyDepthBucket(depthProgress: number, isFocused: boolean): SceneNodeDepthBucket {
	if (isFocused) {
		return 'focus';
	}

	if (depthProgress >= 0.7) {
		return 'near';
	}

	if (depthProgress <= 0.25) {
		return 'far';
	}

	return 'mid';
}

function normalizeDepth(value: number, minimum: number, maximum: number): number {
	if (!Number.isFinite(value)) {
		return 0.5;
	}

	if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || minimum === maximum) {
		return 0.5;
	}

	return Math.min(1, Math.max(0, (value - minimum) / (maximum - minimum)));
}

function radiusForDepth(depthBucket: SceneNodeDepthBucket): number {
	switch (depthBucket) {
		case 'focus':
			return 34;
		case 'near':
			return 28;
		case 'mid':
			return 22;
		case 'far':
		default:
			return 16;
	}
}
